import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon, Train } from 'lucide-react';
import CTA from '../../components/CTA';

const PortadownAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Portadown | Stress Hypnotherapy Commercial Hub</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Portadown from Belfast clinic (30 miles). Evidence-based treatment for anxiety, panic, retail stress. CNHC registered. Railway town support."
        />
        <meta name="keywords" content="anxiety treatment Portadown, stress management Portadown, hypnotherapy anxiety Portadown, panic attacks Portadown County Armagh, retail stress Portadown, therapist Portadown" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/portadown-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Portadown | Stress Hypnotherapy Commercial Hub" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Portadown from Belfast. Evidence-based treatment for anxiety disorders, panic, retail sector stress." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety and Stress Treatment Portadown",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Portadown from Belfast clinic. Specializing in anxiety disorders, panic attacks, retail sector stress, and supporting the commercial hub and railway town community.",
            "url": "https://www.sharonclare-hypnotherapy.com/portadown-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Portadown",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Portadown from your Belfast clinic for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Portadown is approximately 30 miles from the Belfast clinic via the M1 motorway, taking 40-45 minutes by car. This manageable distance makes regular anxiety treatment appointments entirely practical. The railway connection also provides an alternative transport option if you prefer not to drive. Many Portadown clients find the journey worthwhile for specialist anxiety care."
                }
              },
              {
                "@type": "Question",
                "name": "Can I do anxiety sessions online instead of traveling to Belfast?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Portadown residents who prefer the convenience of online therapy or cannot make the journey to Belfast on a particular week. Online sessions provide the same professional, evidence-based anxiety treatment as in-person sessions. This flexibility is ideal for clients with irregular work hours, caring responsibilities, or those who simply prefer online therapy."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for retail workers dealing with anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for retail sector stress and anxiety. Many Portadown residents work in retail at locations like The Meadows Shopping Centre, dealing with long hours on their feet, difficult customers, sales targets, and shift work. Solution-focused hypnotherapy helps calm the stress response, build resilience to customer-facing pressures, improve sleep quality, and develop healthier coping strategies for workplace stress."
                }
              },
              {
                "@type": "Question",
                "name": "How does commuter stress contribute to anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Commuter stress is a significant contributor to anxiety for many Portadown residents who travel to Belfast or other towns for work. The daily journey, traffic pressures, time poverty, and difficulty balancing work demands with family life can trigger constant worry and physical anxiety symptoms. Hypnotherapy helps by calming the stress response, building resilience, improving sleep, and developing healthier thought patterns around work-life balance."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with work-life balance anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for anxiety related to work-life balance challenges. Many people in Portadown's busy commercial hub struggle with the demands of work, family responsibilities, and finding time for themselves. Solution-focused hypnotherapy helps you manage stress, set boundaries, build resilience, and create a healthier relationship with work pressures. You learn to switch off, prioritize effectively, and protect your wellbeing."
                }
              },
              {
                "@type": "Question",
                "name": "Is treatment confidential if I live in a busy town?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. All sessions are completely confidential and protected by professional ethics standards. As a CNHC registered hypnotherapist, I maintain strict confidentiality. Whether you attend in-person at the Belfast clinic or online from home in Portadown, your privacy is fully protected. Nothing discussed in sessions is shared with anyone without your explicit consent (except in rare legal requirements around safeguarding)."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Portadown
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Portadown's commercial hub community from Belfast
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">30 miles</span> from Portadown via M1 • <span className="font-semibold">40-45 min drive</span>
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Train className="w-5 h-5 text-accent-600" />
                <p className="text-gray-600">Railway connection available • Online sessions also offered</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Portadown */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Portadown
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Portadown and struggling with anxiety or stress, you're not alone. As the largest of Craigavon's twin towns with a population of around 32,000, Portadown functions as the commercial hub for the wider Craigavon borough. This busy, bustling character - dominated by retail, hospitality, and the historic railway junction - creates a fast-paced environment that can significantly impact mental health and wellbeing.
              </p>
              <p>
                Many Portadown residents experience anxiety related to work pressures in the dominant retail sector. The Meadows Shopping Centre and surrounding commercial areas employ thousands of people dealing with long hours on their feet, difficult customers, sales targets, and irregular shift patterns. The emotional labor of customer-facing roles - constantly smiling, managing complaints, staying polite when you're exhausted - can be absolutely draining and contribute to chronic stress and anxiety.
              </p>
              <p>
                For those who commute from Portadown to Belfast or other towns for work, there's the added burden of daily travel stress, time poverty, and the exhausting challenge of balancing work demands with family life. The major railway junction on the Dublin-Belfast line means Portadown is well-connected, but it also means residents are pulled in multiple directions - work in one place, home in another, always rushing, never quite having enough time. Add financial worries, family responsibilities, and the physical symptoms of chronic anxiety - tight chest, racing heart, poor sleep, constant tension - and it's no wonder that stress can feel overwhelming.
              </p>
              <p>
                While I don't have a physical clinic in Portadown itself, I'm proud to serve the Portadown community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast. The journey from Portadown is straightforward - approximately 30 miles north via the M1 motorway, taking 40-45 minutes by car. The direct railway connection also provides an alternative transport option if you prefer not to drive. For Portadown residents, this provides accessible specialist anxiety treatment at a manageable distance. For those who prefer the convenience of staying local, or have caring responsibilities or irregular work hours making travel difficult, I also offer online sessions that provide the same professional treatment from the comfort of your home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Portadown Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Difficulty falling or staying asleep',
                    'Racing thoughts, especially at night',
                    'Physical symptoms: tight chest, pounding heart, tense shoulders',
                    'Constant sense of unease or dread',
                    'Avoiding situations out of fear or worry',
                    'Feeling overwhelmed by everyday tasks',
                    'Difficulty concentrating or making decisions',
                    'Irritability and mood swings'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Portadown-Specific Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'Retail sector stress and customer-facing pressure',
                    'Long hours on feet at Meadows Shopping Centre',
                    'Sales targets and performance anxiety',
                    'Irregular shift patterns disrupting sleep',
                    'Railway commuter stress and time poverty',
                    'Work-life balance in busy commercial hub',
                    'Financial anxiety and economic insecurity',
                    'Emotional exhaustion from service roles'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                When anxiety and stress dominate your life, it can feel impossible to escape. The constant worry, the physical tension that never leaves, the exhaustion from being on high alert all the time - it affects your sleep, your relationships, your ability to enjoy life, and your capacity to cope with daily challenges. But you don't have to live like this. There is real, effective help available. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety, helping you regain control and build lasting calm.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety: What's Really Happening
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Anxiety is one of the most common mental health conditions in the UK - around 1 in 4 people will experience an anxiety disorder at some point in their lives. But understanding that it's common doesn't make it any easier to live with. Anxiety is your brain's natural alarm system becoming overactive, interpreting everyday situations as serious threats and triggering your fight-or-flight response even when there's no real danger present.
              </p>
              <p>
                When you're anxious, your brain releases stress hormones like cortisol and adrenaline, preparing your body to respond to a perceived threat. This is why you experience physical symptoms - racing heart, tight chest, shallow breathing, muscle tension, digestive issues, feeling shaky or on edge. Your brain is literally preparing you to fight or run away, even when you're just standing at work dealing with a difficult customer or lying in bed trying to sleep.
              </p>
              <p>
                Anxiety disorders include generalized anxiety disorder (persistent, excessive worry about everyday things), social anxiety (fear of social situations and judgment), panic disorder (sudden, intense panic attacks), and specific phobias. Many people experience a combination of symptoms that don't fit neatly into one category. What matters is not the diagnostic label, but the impact anxiety is having on your quality of life, your relationships, your work, and your ability to function day-to-day.
              </p>
              <p>
                The good news - and this is really important - is that anxiety is highly treatable. Your brain created these anxious patterns through repeated stress and worry, and your brain can learn new, healthier patterns. Solution-focused hypnotherapy works by calming the overactive stress response, helping you understand your triggers, building resilience, and creating lasting change in how you think about and respond to stressful situations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from worry and toward practical, positive change. Hypnosis calms the overactive fight-or-flight response so panic and tension reduce, and clarity returns. It's not about dwelling on the past or analyzing every source of stress - it's about building the resilience and calm you need to move forward with confidence.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms like racing heart, tight chest, and tense muscles. Your body learns to relax again, and the constant state of high alert fades away.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Rewire Thought Patterns',
                  text: 'Build new habits of thought and behavior that support resilience and wellbeing. Replace anxious, catastrophic thinking with calmer, more balanced responses to workplace stress and daily challenges.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Restful Sleep',
                  text: 'Create a calmer pre-sleep routine and quiet the racing mind that keeps you awake despite exhaustion. Wake feeling more refreshed and better equipped to handle the demands of work and family life.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-white rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl text-center">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Generalized anxiety disorder',
                    'Social anxiety and performance pressure',
                    'Panic attacks and panic disorder',
                    'Work-related stress and burnout',
                    'Retail sector anxiety and customer stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Commuter stress and travel anxiety',
                    'Sleep problems from shift work patterns',
                    'Financial anxiety and economic worry',
                    'Work-life balance stress',
                    'Family and relationship tension from stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Sharon's Approach to Anxiety Treatment
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. My background in nursing means I understand the medical and neurological side of anxiety - the brain chemistry, the physical symptoms, the interactions with other health conditions. But more importantly, I understand the human side: the exhaustion, the fear, the desperate need for relief, and the courage it takes to admit you need help and actually reach out.
              </p>
              <p>
                I use solution-focused hypnotherapy, an evidence-based approach that combines talking therapy with hypnosis. Each session begins with structured conversation about your week, focusing on what's working, what you want to achieve, and practical steps forward. This isn't about dwelling on problems or analyzing every source of stress - it's about building momentum toward positive change and identifying what you can control. Then we move into hypnosis, a deeply relaxed state where your subconscious mind becomes more receptive to positive suggestions and new patterns of thinking.
              </p>
              <p>
                Hypnosis isn't sleep, and it's definitely not stage hypnosis where someone makes you cluck like a chicken. You remain completely aware and in control throughout. It's simply a state of focused attention and deep relaxation - similar to being absorbed in a good book or daydreaming on a familiar journey. In this relaxed state, we work with your subconscious mind to calm the overactive anxiety response, build resilience, and create lasting change in how you think about and respond to stressful situations.
              </p>
              <p>
                Most clients notice significant improvements within 4-6 sessions, though everyone's journey is different. Some people feel noticeably calmer after just one or two sessions. Others need a bit more time to build lasting change, especially if anxiety has been present for years. What matters is that you're moving forward at your own pace, supported by evidence-based treatment that addresses both the symptoms and the underlying causes of anxiety. You don't have to live with constant worry, panic, and stress. Help is available, and lasting change is absolutely possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Portadown
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Most Flexible)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M1 motorway northbound into Belfast, then south along the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> 30 miles through Moira and Lisburn
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 40-45 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Public transport:</span> Metro services run along the Ormeau Road corridor, with Botanic and City Hospital the nearest rail stations
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 mb-6">
                <Train className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Train (Alternative Option)</h3>
                  <p className="text-gray-700 mb-2">
                    Portadown is a major junction on the Belfast line with regular services into the city. Botanic and City Hospital are the closest stations to the clinic, with Lanyon Place serving regional routes, and Metro buses run along the Ormeau Road corridor.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Manageable Distance for Regular Treatment</h3>
                <p className="text-gray-700">
                  At 30 miles via the M1 motorway, the journey from Portadown to Belfast is genuinely practical for regular anxiety treatment appointments. Many Portadown clients find the drive manageable and worthwhile for specialist care. Some even share that the journey becomes part of the therapeutic process - dedicated time away from work stress, retail pressures, and family responsibilities to focus on yourself and your wellbeing. The drive there can be time to mentally prepare, and the drive home time to reflect and practice the calm you've built.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-accent-200">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer not to drive to Belfast? Can't make the journey on a particular week due to shift work or caring responsibilities? I offer secure online hypnotherapy sessions via video call. Online sessions provide exactly the same professional, evidence-based anxiety treatment with the convenience of attending from your home in Portadown. This option is ideal for those with mobility concerns, irregular work hours, childcare responsibilities, or anyone who simply prefers the flexibility and comfort of online therapy. The treatment is just as effective - only the location changes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Portadown Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working retail at the Meadows for years, I was completely burnt out. Long hours, difficult customers, never being able to switch off even at home. The anxiety was constant - racing heart, couldn't sleep, snapping at my family. Sharon's hypnotherapy changed everything. After five sessions, I'm sleeping properly, I can handle customer complaints without taking them personally, and I actually feel calm. I'm still in retail, but I cope completely differently now. The anxiety doesn't control me anymore."
                </p>
                <p className="font-semibold text-gray-900">L.R., County Armagh - Retail Sector Anxiety and Burnout</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The panic attacks started out of nowhere - racing heart, couldn't breathe, convinced I was dying. I was avoiding going to work, avoiding social situations, just completely paralyzed by fear. Sharon helped me understand what was happening in my brain and gave me tools to manage it. After six sessions, I haven't had a panic attack in months. I can go to work, I can socialize, I feel like I've got my life back. The online sessions were perfect because I was too anxious to drive to Belfast at first."
                </p>
                <p className="font-semibold text-gray-900">D.M., Craigavon Borough - Panic Attacks and Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Between commuting on the train to Belfast for work, dealing with job pressure, and trying to be there for my family, I was completely overwhelmed. Constant anxiety, couldn't relax, terrible sleep. Sharon's approach helped me manage the stress, set boundaries, and actually switch off when I get home. I'm still doing the same job and the same commute, but I handle it so much better. The anxiety is still there sometimes, but it doesn't overwhelm me like it used to."
                </p>
                <p className="font-semibold text-gray-900">T.H., County Armagh - Work Stress and Commuter Anxiety</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Portadown Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Portadown from your Belfast clinic for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Portadown is approximately 30 miles from the Belfast clinic via the M1 motorway, taking 40-45 minutes by car. This manageable distance makes regular anxiety treatment appointments entirely practical. The railway connection also provides an alternative transport option if you prefer not to drive. Many Portadown clients find the journey worthwhile for specialist anxiety care.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I do anxiety sessions online instead of traveling to Belfast?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Portadown residents who prefer the convenience of online therapy or cannot make the journey to Belfast on a particular week. Online sessions provide the same professional, evidence-based anxiety treatment as in-person sessions. This flexibility is ideal for clients with irregular work hours, caring responsibilities, or those who simply prefer online therapy.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for retail workers dealing with anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for retail sector stress and anxiety. Many Portadown residents work in retail at locations like The Meadows Shopping Centre, dealing with long hours on their feet, difficult customers, sales targets, and shift work. Solution-focused hypnotherapy helps calm the stress response, build resilience to customer-facing pressures, improve sleep quality, and develop healthier coping strategies for workplace stress.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does commuter stress contribute to anxiety?
                </h3>
                <p className="text-gray-700">
                  Commuter stress is a significant contributor to anxiety for many Portadown residents who travel to Belfast or other towns for work. The daily journey, traffic pressures, time poverty, and difficulty balancing work demands with family life can trigger constant worry and physical anxiety symptoms. Hypnotherapy helps by calming the stress response, building resilience, improving sleep, and developing healthier thought patterns around work-life balance.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with work-life balance anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is very effective for anxiety related to work-life balance challenges. Many people in Portadown's busy commercial hub struggle with the demands of work, family responsibilities, and finding time for themselves. Solution-focused hypnotherapy helps you manage stress, set boundaries, build resilience, and create a healthier relationship with work pressures. You learn to switch off, prioritize effectively, and protect your wellbeing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is treatment confidential if I live in a busy town?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. All sessions are completely confidential and protected by professional ethics standards. As a CNHC registered hypnotherapist, I maintain strict confidentiality. Whether you attend in-person at the Belfast clinic or online from home in Portadown, your privacy is fully protected. Nothing discussed in sessions is shared with anyone without your explicit consent (except in rare legal requirements around safeguarding).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Calm"
        subtitle="Take the first step toward less anxiety, less stress, and more peace. Book a free consultation today and discover how hypnotherapy can help you regain control and find calm in your busy life."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default PortadownAnxietyStress;
